import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCourseGenerationRequest } from "@/hooks/useCourseGenerationRequest";
import { ContentGrid } from "@/components/content/ContentGrid";
import { ContentItem, ContentType } from "@/types/content";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquareText, FileText, Link2, Sparkles, Upload } from "lucide-react";

interface CourseSourcesViewProps {
  courseId: string;
  sourceContentIds: string[];
}

const sourceTypeConfig: Record<string, { icon: React.ElementType; label: string; description: string }> = {
  prompt: {
    icon: MessageSquareText,
    label: "Text Prompt",
    description: "This course was generated from a text prompt.",
  },
  paste: {
    icon: FileText,
    label: "Pasted Text",
    description: "This course was generated from pasted text content.",
  },
  content: {
    icon: Link2,
    label: "Linked Content",
    description: "This course was generated from a linked content item.",
  },
  upload: {
    icon: Upload,
    label: "Uploaded File",
    description: "This course was generated from an uploaded file.",
  },
};

export const CourseSourcesView: React.FC<CourseSourcesViewProps> = ({
  courseId,
  sourceContentIds,
}) => {
  const { data: genRequest, isLoading: genLoading } = useCourseGenerationRequest(courseId);

  // Merge sourceContentIds with genRequest.contentId to ensure we catch all linked content
  const allContentIds = React.useMemo(() => {
    const ids = new Set(sourceContentIds);
    if (genRequest?.contentId) {
      ids.add(genRequest.contentId);
    }
    return Array.from(ids);
  }, [sourceContentIds, genRequest?.contentId]);

  // Fetch linked content items
  const { data: linkedContent, isLoading: contentLoading } = useQuery({
    queryKey: ["course-source-content", courseId, allContentIds],
    queryFn: async () => {
      if (allContentIds.length === 0) return [];
      const { data, error } = await supabase
        .from("content")
        .select("*")
        .in("id", allContentIds);
      if (error) throw error;
      return (data ?? []).map(
        (item): ContentItem => ({
          id: item.id,
          title: item.title,
          type: item.type as ContentType,
          createdAt: item.created_at,
          thumbnailUrl: item.thumbnail_url,
          sourceUrl: item.source_url,
          storagePath: item.storage_path,
          roomId: item.room_id,
          updatedAt: item.updated_at,
          visibility: item.visibility as "private" | "public",
        }),
      );
    },
    enabled: allContentIds.length > 0 || (genRequest !== undefined && !genLoading),
  });

  if (genLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  const sourceKey = genRequest?.source ?? "";
  const config = sourceTypeConfig[sourceKey] ?? {
    icon: Sparkles,
    label: "AI Generated",
    description: "This course was generated by AI.",
  };
  const Icon = config.icon;

  const hasLinkedContent = allContentIds.length > 0;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Generation source card */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              Generated from {config.label}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{config.description}</p>
            {genRequest?.prompt && (
              <div className="mt-3 rounded-xl bg-muted/50 border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Prompt
                </p>
                <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap line-clamp-8">
                  {genRequest.prompt}
                </p>
              </div>
            )}
            {!genRequest && (
              <p className="mt-1 text-sm text-muted-foreground">
                No generation details available for this course.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Linked content items */}
      {hasLinkedContent && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Source Materials
          </h3>
          {contentLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {allContentIds.map((id) => (
                <Skeleton key={id} className="h-28 w-full rounded-2xl" />
              ))}
            </div>
          ) : linkedContent && linkedContent.length > 0 ? (
            <ContentGrid
              items={linkedContent}
              isLoading={false}
              skeletonCount={allContentIds.length}
            />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-10 text-center">
              <FileText className="mb-3 h-7 w-7 text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">
                Source content not accessible
              </p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                The original file may have been deleted or is unavailable.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty state — no content linked at all */}
      {!hasLinkedContent && !genRequest?.contentId && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-12 text-center">
          <Sparkles className="mb-3 h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm font-medium text-muted-foreground">
            No source materials linked
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            This course was generated without reference files.
          </p>
        </div>
      )}
    </div>
  );
};
